import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { setUnauthorizedHandler } from './api/auth-events'
import { clearOutboxAccount, flushOutbox, setOutboxAccount } from './offline/outbox'
import { invalidateAfterFlush } from './offline/flushInvalidate'
import { AppShell } from './app/AppShell'
import { ProtectedRoute } from './app/ProtectedRoute'
import { LoginPage } from './pages/LoginPage'
import { MemberOnboardingPage } from './onboarding/MemberOnboardingPage'
import { WorkshopRegisterPage } from './onboarding/WorkshopRegisterPage'
import { ForgotPasswordPage } from './onboarding/ForgotPasswordPage'
import { ResetCodePage } from './onboarding/ResetCodePage'
import { ChefActivationPage } from './onboarding/ChefActivationPage'
import { PublicEstimatePage } from './estimate/PublicEstimatePage'
import { LegalPage } from './legal/LegalPage'
import { CustomersPage } from './customers/CustomersPage'
import { EinkaufBoard } from './einkauf/EinkaufBoard'
import { EstimatePrintPage } from './invoice/EstimatePrintPage'
import { InvoiceDocPage } from './invoice/InvoiceDocPage'
import { InvoicePrintPage } from './invoice/InvoicePrintPage'
import { AboInvoicePrintPage } from './billing/AboInvoicePrintPage'
import { InventoryPage } from './inventory/InventoryPage'
import { ArchivePage } from './orders/ArchivePage'
import { IntakeBoard } from './orders/IntakeBoard'
import { InvoicesPage } from './orders/InvoicesPage'
import { OpenItemsPage } from './orders/OpenItemsPage'
import { MyJobsList } from './orders/MyJobsList'
import { NewOrderPage } from './orders/NewOrderPage'
import { OrderDetail } from './orders/OrderDetail'
import { OrdersBoard } from './orders/OrdersBoard'
import { WorkshopBoard } from './orders/WorkshopBoard'
import { OverviewPage } from './overview/OverviewPage'
import { PlanungBoard } from './planung/PlanungBoard'
import { ZeitenPage } from './zeiten/ZeitenPage'
import { Art13Page } from './zeiten/Art13Page'
import { KioskPage } from './kiosk/KioskPage'
import { PreisePage } from './prices/PreisePage'
import { SettingsPage } from './settings/SettingsPage'
import { BillingPage } from './billing/BillingPage'
import { AdminPage } from './admin/AdminPage'
import { AccountPage } from './account/AccountPage'
import { TeamPage } from './team/TeamPage'
import { AdminWorkshopDetail } from './admin/AdminWorkshopDetail'
import { AdminAuditPage } from './admin/AdminAuditPage'
import { ProtokollPage } from './protokoll/ProtokollPage'
import { useAuth } from './app/auth'

/** Landung nach Login: der Super-Admin (kein Mandant) startet in der App-Verwaltung, alle anderen in der Übersicht. */
function HomeRedirect() {
  const { user } = useAuth()
  return <Navigate to={user?.isSuperadmin ? '/admin' : '/overview'} replace />
}

export function App() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    // DER eine zentrale 401-Handler (#1): Session-State leeren + zurück zum Login.
    setUnauthorizedHandler(() => {
      qc.clear()
      navigate('/login', { replace: true })
    })
    return () => setUnauthorizedHandler(null)
  }, [qc, navigate])

  // Outbox an das eingeloggte Konto binden (R2/1.5): auf einem geteilten Tablet darf die Offline-Queue von
  // Konto A nie unter der Session von Konto B abgespielt werden. Beim Login/Boot das Konto setzen (übernimmt
  // konto-lose Alt-Items) und dessen Queue unter DESSEN Session zustellen; beim Logout die Bindung lösen.
  useEffect(() => {
    const id = user?.id
    if (id == null) {
      clearOutboxAccount()
      return
    }
    setOutboxAccount(id)
    void flushOutbox().then((result) => invalidateAfterFlush(qc, result))
  }, [user?.id, qc])

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrieren" element={<MemberOnboardingPage />} />
      <Route path="/werkstatt-registrieren" element={<WorkshopRegisterPage />} />
      <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
      <Route path="/passwort-zuruecksetzen" element={<ResetCodePage />} />
      <Route path="/aktivieren" element={<ChefActivationPage />} />
      <Route path="/freigabe/:token" element={<PublicEstimatePage />} />
      {/* Kiosk-Terminal: bewusst AUSSERHALB von ProtectedRoute/AppShell — das Wandtablet hat keine Session,
          es authentifiziert pro Vorgang mit Geräte-Token + persönlicher PIN. Eine Route innerhalb der Shell
          hätte die komplette App des zuletzt Angemeldeten in der Halle offengelegt. */}
      <Route path="/terminal" element={<KioskPage />} />
      <Route path="/impressum" element={<LegalPage title="Impressum" />} />
      <Route path="/datenschutz" element={<LegalPage title="Datenschutzerklärung" />} />
      <Route path="/avv" element={<LegalPage title="Auftragsverarbeitungsvertrag (AVV)" />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/print/invoice/:id" element={<InvoicePrintPage />} />
        <Route path="/print/estimate/:id" element={<EstimatePrintPage />} />
        <Route path="/print/invoice-doc/:invoiceId" element={<InvoiceDocPage />} />
        <Route path="/print/abo-invoice/:id" element={<AboInvoicePrintPage />} />
        <Route path="/" element={<AppShell />}>
          <Route index element={<HomeRedirect />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="orders" element={<OrdersBoard />} />
          <Route path="new" element={<NewOrderPage />} />
          <Route path="intake" element={<IntakeBoard />} />
          <Route path="workshop" element={<WorkshopBoard />} />
          <Route path="planung" element={<PlanungBoard />} />
          <Route path="my" element={<MyJobsList />} />
          <Route path="zeiten" element={<ZeitenPage />} />
          <Route path="zeiten/datenschutz" element={<Art13Page />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="einkauf" element={<EinkaufBoard />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="archive" element={<ArchivePage />} />
          <Route path="open" element={<OpenItemsPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="prices" element={<PreisePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="einstellungen/abo" element={<BillingPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/workshops/:id" element={<AdminWorkshopDetail />} />
          <Route path="admin/audit" element={<AdminAuditPage />} />
          <Route path="protokoll" element={<ProtokollPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="orders/:id" element={<OrderDetail />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
